import { graphql, useFragment } from 'react-relay';
import { useUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { getIsFreeShipping } from './shippingCostHelpers';
import { DEFAULT_SHIPPING_BADGE_TYPE, FREE_SHIPPING, FREE_SHIPPING_AND_COST } from '../constants';

import { useShippingPrice_viewer$key } from './__generated__/useShippingPrice_viewer.graphql';
import { useShippingPrice_item$key } from './__generated__/useShippingPrice_item.graphql';

const viewerFragment = graphql`
    fragment useShippingPrice_viewer on Viewer
    @argumentDefinitions(
        fetchRegionalInfo: { type: "Boolean!", defaultValue: false }
        userZipCode: { type: "String", defaultValue: "" }
        userCountryCode: { type: "String", defaultValue: "" }
    ) {
        regionsInfo: regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
            @include(if: $fetchRegionalInfo) {
            regionsByZipCode {
                displayName
            }
        }
    }
`;

const itemFragment = graphql`
    fragment useShippingPrice_item on Item
    @argumentDefinitions(
        buyerId: { type: "String", defaultValue: "" }
        fetchShippingCostInfo: { type: "Boolean", defaultValue: false }
        fetchLiveShipmentQuote: { type: "Boolean", defaultValue: true }
        itemQuantity: { type: "Int" }
    ) {
        ...shippingCostHelpers_item
        relevantShippingInfo(
            buyerId: $buyerId
            useLiveEndpoint: $fetchLiveShipmentQuote
            quantity: $itemQuantity
        ) @include(if: $fetchShippingCostInfo) {
            shipmentQuote {
                buyerTotalAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
        }
    }
`;

export const useShippingPrice = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
    type = DEFAULT_SHIPPING_BADGE_TYPE,
}: {
    viewer: useShippingPrice_viewer$key | null;
    item: useShippingPrice_item$key | null;
    currency: string;
    //when using FREE_SHIPPING_AND_COST type we need to provide buyerId, fetchShippingCostInfo variables to relay fragment
    type?: typeof FREE_SHIPPING | typeof FREE_SHIPPING_AND_COST;
}): number | null => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const userSessionRegions = useUserSessionRegions();

    if (!item) {
        return null;
    }

    const userRegions = userSessionRegions || viewer?.regionsInfo?.[0]?.regionsByZipCode || [];
    const relevantQuote = item.relevantShippingInfo?.shipmentQuote;

    const { amount } =
        relevantQuote?.buyerTotalAmount?.convertedAmountList?.find(p => p?.currency === currency) ||
        {};
    const isFreeShipping = getIsFreeShipping({ item, userRegions });

    if (isFreeShipping || (type === FREE_SHIPPING_AND_COST && amount === 0)) {
        return 0;
    }

    if (type === FREE_SHIPPING_AND_COST && amount) {
        return amount;
    }

    return null;
};
